/**
 * ⟦C57 · lot 8 · A-174⟧ **« Les dessous » d'un mini-jeu** : le bouton, et le dialogue partagé
 * (`components/LesDessous.tsx`), posés sur l'écran de consigne, l'entre-deux ou le bilan d'une étape.
 *
 * Il porte ce que l'écran ne dit plus : la règle entière, les commandes au toucher, ce que fait
 * chaque variante, et **la leçon de Matt, entière** (`LECONS_MINIJEU` : les répliques, les
 * paragraphes, « à retenir »). Après la première fois, la leçon ne passe plus par l'intro de l'étape :
 * c'est ici qu'on la relit, quand on veut.
 *
 * ## Jamais pendant un chrono
 *
 * Les phases chronométrées (le jeu, le repérage, la promesse, le métrage) n'offrent PAS ce bouton :
 * ouvrir un dialogue pendant que le temps court serait une punition. Toute l'explication d'une phase
 * chronométrée vit donc dans les dessous de l'écran de consigne qui la précède, lu à tête reposée.
 *
 * `children` : les blocs propres à l'écran (`DessousBloc`), posés AVANT la leçon.
 */

import { createContext, useContext, type ReactNode } from "react";

import type { AtelierStage } from "@maxime/shared";

import { DessousBloc, LesDessous } from "../../components/LesDessous";
import { LECONS_MINIJEU } from "../../game/decouverte";

/** La leçon de Matt, entière : les répliques d'origine, le détail, « à retenir ». Rien n'est retiré. */
export function BlocsLeconMiniJeu({ stage }: { stage: AtelierStage }) {
  const l = LECONS_MINIJEU[stage];
  return (
    <>
      <DessousBloc titre="Ce que dit Matt">
        {(l.repliques ?? [l.aRetenir]).map((r, i) => (
          <p className="dessous-texte" key={i}>
            {r}
          </p>
        ))}
      </DessousBloc>
      <DessousBloc titre="Le détail">
        {l.corps.map((c, i) => (
          <p className="dessous-texte" key={i}>
            {c}
          </p>
        ))}
        <p className="dessous-texte">
          <strong>À retenir :</strong> {l.aRetenir}
        </p>
      </DessousBloc>
    </>
  );
}

/**
 * ⟦C62 · lot atelier · friction 3⟧ **Les blocs que l'Atelier ajoute aux dessous du mini-jeu** (les aides actives, le
 * matos, la délégation, ce que coûte la production) : ils vivaient dans les dessous de l'écran d'intro de Matt, qui
 * n'existe plus. Un contexte et pas une prop : les cinq mini-jeux n'ont pas à savoir qu'on leur en passe, et il n'y a
 * toujours qu'UN « Les dessous » par écran. `null` (le défaut, un mini-jeu monté seul) : rien en plus.
 */
export const DessousEnPlus = createContext<ReactNode>(null);

export function DessousMiniJeu({
  stage,
  children,
  variante = "texte",
  className,
}: {
  stage: AtelierStage;
  children?: ReactNode;
  variante?: "texte" | "icone";
  className?: string;
}) {
  const l = LECONS_MINIJEU[stage];
  const enPlus = useContext(DessousEnPlus);
  return (
    <LesDessous titre={`${l.icone} ${l.titre}`} variante={variante} className={className}>
      {children}
      {enPlus}
      <BlocsLeconMiniJeu stage={stage} />
    </LesDessous>
  );
}
